import {
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import type {
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent,
  StripeElementStyle,
} from "@stripe/stripe-js";
import { isApiError } from "../api";
import { PlatformWalletElementsProvider } from "./PlatformWalletElementsProvider";

const CARD_STYLE_FALLBACKS = {
  text: "#0F172A",
  muted: "#475569",
  error: "#DC2626",
  success: "#059669",
  fontFamily: "system-ui, -apple-system, sans-serif",
  fontSize: "14px",
} as const;

export type AddCardField = "number" | "expiry" | "cvc";

type CardFieldErrors = Record<AddCardField, string | null>;
type CardFieldCompletion = Record<AddCardField, boolean>;
type ConfirmedCard = {
  paymentMethodId: string;
  idempotencyKey: string;
};

type CardFieldChangeEvent =
  | StripeCardNumberElementChangeEvent
  | StripeCardExpiryElementChangeEvent
  | StripeCardCvcElementChangeEvent;

export interface AddCardFormRenderModel {
  fieldsRef: RefObject<HTMLDivElement | null>;
  fieldsLocked: boolean;
  focusedField: AddCardField | null;
  fields: {
    number: { id: string; label: string; element: ReactNode };
    expiry: { id: string; label: string; element: ReactNode };
    cvc: { id: string; label: string; element: ReactNode };
  };
  postal: {
    id: string;
    value: string;
    disabled: boolean;
    onChange: (value: string) => void;
  };
  error: string | null;
  saveDisabledReason: string | null;
  saveDisabledReasonId: string;
  submit: {
    disabled: boolean;
    label: string;
    onClick: () => void;
  };
}

export interface AddCardFormProps {
  /** Platform publishable key already fetched by checkout; optional for standalone use. */
  publishableKey?: string;
  unavailableFallback?: ReactNode;
  /** Focus the hosted card-number field when this form is explicitly opened. */
  autoFocus?: boolean;
  /** Mint a platform SetupIntent for the selected player's canonical saved cards. */
  createSetupIntent: (idempotencyKey: string) => Promise<string>;
  /** Reports the complete setup + parent reconciliation window. */
  onBusyChange?: (busy: boolean) => void;
  /** Called with the newly-created payment_method id after a confirmed setup. */
  onCardAdded: (
    paymentMethodId: string,
    idempotencyKey: string,
  ) => void | Promise<void>;
  /** App-native presentation. Stripe setup state stays authoritative here;
   * the consuming app only renders the supplied field/control model. */
  render: (model: AddCardFormRenderModel) => ReactNode;
}

const TERMINAL_SETUP_INTENT_ERROR_CODES = new Set([
  "intent_invalid_state",
  "setup_intent_unexpected_state",
]);

const EMPTY_FIELD_ERRORS: CardFieldErrors = {
  number: null,
  expiry: null,
  cvc: null,
};

const EMPTY_FIELD_COMPLETION: CardFieldCompletion = {
  number: false,
  expiry: false,
  cvc: false,
};

function isTerminalSetupIntentCreationError(error: unknown) {
  return (
    isApiError(error) &&
    error.status >= 400 &&
    error.status < 500 &&
    error.status !== 408 &&
    error.status !== 429
  );
}

function resolveToken(
  elementStyles: CSSStyleDeclaration | null,
  rootStyles: CSSStyleDeclaration | null,
  name: string,
  fallback: string,
) {
  return (
    elementStyles?.getPropertyValue(name).trim() ||
    rootStyles?.getPropertyValue(name).trim() ||
    fallback
  );
}

function resolveFirstToken(
  elementStyles: CSSStyleDeclaration | null,
  rootStyles: CSSStyleDeclaration | null,
  names: readonly string[],
  fallback: string,
) {
  for (const name of names) {
    const value =
      elementStyles?.getPropertyValue(name).trim() ||
      rootStyles?.getPropertyValue(name).trim();
    if (value) return value;
  }
  return fallback;
}

function resolveCardStyle(element: HTMLElement | null): StripeElementStyle {
  const canReadStyles = typeof window !== "undefined";
  const elementStyles =
    canReadStyles && element ? window.getComputedStyle(element) : null;
  const rootStyles =
    canReadStyles && typeof document !== "undefined"
      ? window.getComputedStyle(document.documentElement)
      : null;
  const text = resolveFirstToken(
    elementStyles,
    rootStyles,
    ["--mui-palette-semantic-onSurface", "--mui-palette-text-primary", "--foreground", "--ink"],
    CARD_STYLE_FALLBACKS.text,
  );
  const muted = resolveFirstToken(
    elementStyles,
    rootStyles,
    ["--mui-palette-text-secondary", "--muted-foreground", "--ink-2"],
    CARD_STYLE_FALLBACKS.muted,
  );
  const error = resolveFirstToken(
    elementStyles,
    rootStyles,
    ["--mui-palette-semantic-error", "--destructive", "--err-fg"],
    CARD_STYLE_FALLBACKS.error,
  );
  const success = resolveFirstToken(
    elementStyles,
    rootStyles,
    ["--mui-palette-semantic-success", "--primary", "--ok"],
    CARD_STYLE_FALLBACKS.success,
  );

  const useMobileInputSize =
    canReadStyles &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(max-width: 480px)").matches;

  return {
    base: {
      color: text,
      iconColor: muted,
      fontFamily: resolveFirstToken(
        elementStyles,
        rootStyles,
        ["--grotesk", "--font-sans"],
        CARD_STYLE_FALLBACKS.fontFamily,
      ),
      fontSize: useMobileInputSize
        ? "16px"
        : resolveToken(
            elementStyles,
            rootStyles,
            "--fs-md",
            CARD_STYLE_FALLBACKS.fontSize,
          ),
      fontSmoothing: "antialiased",
      "::placeholder": { color: muted },
      ":-webkit-autofill": { color: text },
    },
    complete: {
      color: text,
      iconColor: success,
    },
    invalid: {
      color: error,
      iconColor: error,
    },
  };
}

/**
 * Add a NEW card via Stripe's explicit card fields + confirmCardSetup.
 * Canonical shared copy used by every card-payment surface.
 *
 * Flow: mint a SetupIntent (page hook) → confirmCardSetup(client_secret, card)
 * in the browser against the platform account → hand the opaque wallet card
 * reference back up. The form owns its platform Elements boundary; any sibling
 * bank-authentication control keeps its connected-account context.
 */
export function AddCardForm(props: AddCardFormProps) {
  return (
    <PlatformWalletElementsProvider
      publishableKey={props.publishableKey}
      unavailableFallback={props.unavailableFallback ?? null}
    >
      <PlatformCardFields {...props} />
    </PlatformWalletElementsProvider>
  );
}

function PlatformCardFields({
  autoFocus = false,
  createSetupIntent,
  onBusyChange,
  onCardAdded,
  render,
}: AddCardFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const fieldsRef = useRef<HTMLDivElement>(null);
  const fieldIdPrefix = useId();
  const numberFieldId = `${fieldIdPrefix}-number`;
  const expiryFieldId = `${fieldIdPrefix}-expiry`;
  const cvcFieldId = `${fieldIdPrefix}-cvc`;
  const postalFieldId = `${fieldIdPrefix}-postal`;
  const saveDisabledReasonId = `${fieldIdPrefix}-save-disabled-reason`;
  const [busy, setBusy] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [confirmedCard, setConfirmedCard] = useState<ConfirmedCard | null>(
    null,
  );
  const [focusedField, setFocusedField] = useState<AddCardField | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] =
    useState<CardFieldErrors>(EMPTY_FIELD_ERRORS);
  const [fieldCompletion, setFieldCompletion] = useState<CardFieldCompletion>(
    EMPTY_FIELD_COMPLETION,
  );
  const [postalCode, setPostalCode] = useState("");
  const [postalError, setPostalError] = useState<string | null>(null);
  const [cardStyle, setCardStyle] = useState<StripeElementStyle>(() =>
    resolveCardStyle(null),
  );
  const mountedRef = useRef(true);
  const inFlightRef = useRef(false);
  const onBusyChangeRef = useRef(onBusyChange);
  const clientSecretRef = useRef<string | null>(null);
  const idempotencyKeyRef = useRef(globalThis.crypto.randomUUID());
  onBusyChangeRef.current = onBusyChange;

  function rotateOperation() {
    if (mountedRef.current) setConfirmedCard(null);
    clientSecretRef.current = null;
    idempotencyKeyRef.current = globalThis.crypto.randomUUID();
  }

  function reopenCompletedOperation() {
    if (!completed) return;
    setCompleted(false);
    rotateOperation();
  }

  useLayoutEffect(() => {
    mountedRef.current = true;
    const refreshCardStyle = () => {
      setCardStyle(resolveCardStyle(fieldsRef.current));
    };
    refreshCardStyle();

    const observer =
      typeof MutationObserver === "undefined"
        ? null
        : new MutationObserver(refreshCardStyle);
    observer?.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "data-theme", "style"],
    });
    const mobileQuery =
      typeof window.matchMedia === "function"
        ? window.matchMedia("(max-width: 480px)")
        : null;
    mobileQuery?.addEventListener("change", refreshCardStyle);
    return () => {
      mountedRef.current = false;
      if (inFlightRef.current) onBusyChangeRef.current?.(false);
      observer?.disconnect();
      mobileQuery?.removeEventListener("change", refreshCardStyle);
    };
  }, []);

  async function handleAdd() {
    if (inFlightRef.current) return;
    if (!confirmedCard && (!stripe || !elements)) return;
    const card = confirmedCard ? null : elements?.getElement(CardNumberElement);
    if (!confirmedCard && !card) return;

    setError(null);
    const normalizedPostalCode = postalCode.trim();
    if (!confirmedCard && !normalizedPostalCode) {
      setPostalError("Enter your ZIP or postal code.");
      return;
    }

    inFlightRef.current = true;
    if (mountedRef.current) {
      setBusy(true);
      onBusyChangeRef.current?.(true);
    }
    setPostalError(null);
    try {
      if (confirmedCard) {
        try {
          await onCardAdded(
            confirmedCard.paymentMethodId,
            confirmedCard.idempotencyKey,
          );
          if (mountedRef.current) {
            setCompleted(true);
            setError(null);
          }
        } catch {
          if (mountedRef.current) {
            setError(
              "Card saved, but it is not available yet. Retry card refresh.",
            );
          }
        }
        return;
      }
      if (!stripe || !card) return;

      const idempotencyKey = idempotencyKeyRef.current;
      let clientSecret = clientSecretRef.current;
      if (clientSecret === null) {
        try {
          clientSecret = await createSetupIntent(idempotencyKey);
        } catch (setupError) {
          if (isTerminalSetupIntentCreationError(setupError)) {
            rotateOperation();
          }
          throw setupError;
        }
        if (!mountedRef.current) return;
        clientSecretRef.current = clientSecret;
      }
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card,
          billing_details: {
            address: { postal_code: normalizedPostalCode },
          },
        },
      });
      if (!mountedRef.current) return;
      if (result.error) {
        if (
          result.error.code &&
          TERMINAL_SETUP_INTENT_ERROR_CODES.has(result.error.code)
        ) {
          rotateOperation();
        }
        setError(result.error.message ?? "We couldn't save that card.");
        return;
      }
      const pmId = result.setupIntent?.payment_method;
      if (typeof pmId === "string") {
        const nextConfirmedCard = {
          paymentMethodId: `wallet:${pmId}`,
          idempotencyKey,
        };
        setConfirmedCard(nextConfirmedCard);
        try {
          await onCardAdded(
            nextConfirmedCard.paymentMethodId,
            nextConfirmedCard.idempotencyKey,
          );
          if (mountedRef.current) {
            setCompleted(true);
            setError(null);
          }
        } catch {
          if (mountedRef.current) {
            setError(
              "Card saved, but it is not available yet. Retry card refresh.",
            );
          }
        }
      } else {
        rotateOperation();
        setError("We couldn't save that card. Please try again.");
      }
    } catch (e) {
      if (mountedRef.current) {
        setError(
          isApiError(e)
            ? e.message
            : "We couldn't save that card. Please try again.",
        );
      }
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) {
        setBusy(false);
        onBusyChangeRef.current?.(false);
      }
    }
  }

  function handleFieldChange(field: AddCardField, event: CardFieldChangeEvent) {
    reopenCompletedOperation();
    setError(null);
    setFieldErrors((current) => ({
      ...current,
      [field]: event.error?.message ?? null,
    }));
    setFieldCompletion((current) => ({
      ...current,
      [field]: event.complete,
    }));
  }

  const cardError = fieldErrors.number ?? fieldErrors.expiry ?? fieldErrors.cvc;
  const displayedError = cardError ?? postalError ?? error;
  const cardFieldsComplete = Object.values(fieldCompletion).every(Boolean);
  const reconciliationPending = confirmedCard !== null && !completed;
  const fieldsLocked = busy || reconciliationPending;
  const canSubmit =
    Boolean(stripe && elements) &&
    !busy &&
    !completed &&
    (reconciliationPending ||
      (cardFieldsComplete && Boolean(postalCode.trim())));
  const saveDisabledReason =
    canSubmit || busy || completed || reconciliationPending
      ? null
      : !stripe || !elements
        ? "Card entry is still loading. Wait to save this card."
        : !cardFieldsComplete && !postalCode.trim()
          ? "Complete the card fields and enter your ZIP or postal code to save this card."
          : !cardFieldsComplete
            ? "Complete the card fields to save this card."
            : "Enter your ZIP or postal code to save this card.";
  const submitLabel = busy
    ? reconciliationPending
      ? "Refreshing…"
      : "Saving…"
    : completed
      ? "Card saved"
      : reconciliationPending
        ? "Retry card refresh"
        : "Save card";
  const numberElement = (
    <CardNumberElement
      id={numberFieldId}
      options={{
        disableLink: true,
        disabled: fieldsLocked,
        showIcon: true,
        style: cardStyle,
      }}
      onBlur={() => setFocusedField(null)}
      onChange={(event) => handleFieldChange("number", event)}
      onFocus={() => setFocusedField("number")}
      onReady={(element) => {
        if (autoFocus) element.focus();
      }}
    />
  );
  const expiryElement = (
    <CardExpiryElement
      id={expiryFieldId}
      options={{ disabled: fieldsLocked, style: cardStyle }}
      onBlur={() => setFocusedField(null)}
      onChange={(event) => handleFieldChange("expiry", event)}
      onFocus={() => setFocusedField("expiry")}
    />
  );
  const cvcElement = (
    <CardCvcElement
      id={cvcFieldId}
      options={{ disabled: fieldsLocked, style: cardStyle }}
      onBlur={() => setFocusedField(null)}
      onChange={(event) => handleFieldChange("cvc", event)}
      onFocus={() => setFocusedField("cvc")}
    />
  );
  const handlePostalChange = (value: string) => {
    reopenCompletedOperation();
    setError(null);
    setPostalCode(value);
    if (value.trim()) setPostalError(null);
  };

  return render({
    fieldsRef,
    fieldsLocked,
    focusedField,
    fields: {
      number: {
        id: numberFieldId,
        label: "Card number",
        element: numberElement,
      },
      expiry: {
        id: expiryFieldId,
        label: "Expiration date",
        element: expiryElement,
      },
      cvc: {
        id: cvcFieldId,
        label: "Security code",
        element: cvcElement,
      },
    },
    postal: {
      id: postalFieldId,
      value: postalCode,
      disabled: fieldsLocked,
      onChange: handlePostalChange,
    },
    error: displayedError,
    saveDisabledReason,
    saveDisabledReasonId,
    submit: {
      disabled: !canSubmit,
      label: submitLabel,
      onClick: () => void handleAdd(),
    },
  });
}
